import { expandLocales, type Translate } from './localized';
import { getLang, type Lang } from './i18n';
import type { AcetateFamily } from '../config/acetates';

type Trio = readonly [string, string, string];
/** Pill labels: Taille · Couleurs · Modèles · Je la veux (A110V). */
type Quad = readonly [string, string, string, string];
export interface Words {
  pill: Quad; toolbar: string; help: string; helpBody: string; helpNote: string; helpRear: string;
  headQuestion: string; faces: Trio; facesKept: Trio; frameSize: string; frameSizeShort: string;
  pdEstimated: (mm: number) => string; pdKnown: (mm: number) => string; pdCalibrating: string;
  pdOpen: string; pdClose: string; pdInput: string; apply: string; useFace: string;
  tabs: Trio; sections: readonly [string, string, string, string]; mainAcetate: string; gradient: string;
  intensity: readonly [string, string]; original: string; originalColours: string; materialsNote: string;
  /** A202: the Sunset supplement on a Signature (« Supplément Sunset : + Rp 990.000 »). */
  sunsetSupplement: (price: string) => string;
  acetates: Record<string, string>;
  /** A110V: family subheadings of the acetate chips (desktop grid). */
  acetateFamilies: Record<AcetateFamily, string>;
  lenses: Record<string, string>;
  kept: (face: string | null, pd: number, size: string | null) => string;
  collection: (line: string, n: number) => string; view: (model: string) => string; models: string; closePanel: string;
  /** A42F: « Tout voir » (mobile row) and « Voir tous les modèles » (desktop rail). */
  seeAll: string; seeAllFrames: string;
  /** A200: the Handmade / Signature switch and the shape filter. */
  collections: CollectionWords;
  /** A110T: « Voir le paysage » — rear camera through the Sunset lens (short label for narrow phones). */
  lookThrough: string; lookThroughShort: string;
  /** A110W: shown over the camera image while the glasses are being prepared (first seconds). */
  preparing: string;
  /** A110X: same place, when the try-on comes back after the phone slept. */
  resuming: string;
  /** A110X: the try-on did not recover on its own after a sleep: message and its button. */
  recoverText: string; recoverButton: string;
  /** A110Y: the problem came back, or is beyond a relaunch (3D refused twice, memory, restarts): the page must be reloaded. */
  reloadText: string; reloadButton: string;
  lensView: LensViewWords;
  /** A110V « Je veux cette paire »: photo, specs, WhatsApp / photo / cart actions. */
  order: OrderWords;
  /** A110Z: temple engraving (Couleurs › Gravure on phones, Gravure tab on desktop). */
  engrave: EngraveWords;
}
export interface CollectionWords {
  /** Accessible name of the Handmade / Signature switch. */
  switchLabel: string;
  /** « Forme » filter: its accessible name, « Toutes », and the shape names (keys: Shopify values). */
  shape: string; allShapes: string; shapes: Record<string, string>;
  /** Footer / count while a shape is chosen: « 8 montures · Ronde ». */
  filtered: (n: number, shape: string) => string;
}
export interface EngraveWords {
  /** Tab / panel name and the screen title. */
  tab: string; title: string;
  /** Mobile header: back to the try-on, reset to the house engraving. */
  back: string; original: string;
  /** « {MODÈLE} · Vos branches » card title (model name is data). */
  yourTemples: (model: string) => string;
  inner: string; outer: string;
  /** « B1 · Branche gauche », « B2 · Branche droite ». */
  temple: (side: 'l' | 'r') => string;
  slots: { il: string; ir: string; el: string; er: string };
  alwaysEngraved: string; optional: string;
  /** Empty zone prompt (touch / click). */
  tapToEngrave: string; clickToEngrave: string;
  /** « ↺ Remettre « Handcrafted in Bali » ». */
  restoreDefault: string;
  counter: (n: number, max: number) => string;
  tooLong: string; stripped: string;
  allowed: (symbols: number) => string;
  colour: string;
  inks: { or: string; argent: string; blanc: string; noir: string; rouge: string };
  lowContrast: string;
  font: string;
  filters: { all: string; minimal: string; serif: string; script: string; fort: string; machine: string };
  /** Sample word of the font cards. */
  sample: string;
  symbols: string; addSymbol: (glyph: string) => string;
  symbolFamilies: { ile: string; amour: string; ciel: string; signes: string };
  abc: string; ok: string; erase: string;
  /** A110Z2: letter height slider (mm notches), its − / + buttons, the « same height » link, the limit notes. */
  size: string; smaller: string; larger: string; mm: (v: number) => string; sameHeight: string;
  limitThin: (mm: string) => string; limitLong: (mm: string) => string;
  /** Letter height in the order (« lettres 2,5 mm »). */
  capOrder: (mm: string) => string;
  /**
   * A110Z3: placement by hand — tilt slider and its ↺ / ↻ buttons, degrees, the four arrows, « Recentrer »,
   * the drag hint and the two warnings (off the temple / on the hinge).
   */
  position: string; tilt: string; tiltLeft: string; tiltRight: string; deg: (v: number) => string;
  moveLeft: string; moveUp: string; moveDown: string; moveRight: string; recentre: string; dragHint: string; dragHintBand: string;
  issueOverflow: string; issueHinge: string;
  /**
   * A text placed by hand, for the workshop: centre at `x` mm from the hinge, `y` mm lower (+) or higher (−)
   * than the temple's middle line, tilted `rot`° (clockwise as seen = the text goes down as it is read).
   */
  placeOrder: (x: number, y: number, rot: number) => string;
  /** Order photo: title of the engraved temples sheet, its note, « Branche gauche · intérieur ». */
  sheetTitle: string; sheetNote: string; sheetView: (side: 'l' | 'r', face: 'int' | 'ext') => string;
  /** Desktop sections. */
  texts: string; recap: string; noCustom: string; emptied: string;
  /** Desktop band hint. */
  bandHint: string;
  validate: string;
  /** Toast after a model change with a custom engraving. */
  kept: (model: string) => string;
  /** « Je la veux »: the Gravure line, its value, the reserved price line, the WhatsApp hint. */
  orderLabel: string; textsCount: (n: number) => string; priceNote: string; whatsappHint: string; edit: string;
  /** Cart line-item property names. */
  cart: { font: string; ink: string; slot: (slot: 'il' | 'ir' | 'el' | 'er') => string };
  /** A201 — easier engraving (David, 8 October) and « Je la veux » with every detail. */
  moved: string; undo: string; copyOther: string; copied: (to: string) => string;
  quick: { label: string; hinge: string; middle: string; ear: string };
  adjust: string; logoSize: string; logoTilt: string; logoHint: string; zoomHint: string; zoomClose: string;
  /** A203: the round handle that turns the selected text on the card; the button that lowers / shows the band of cards. */
  spin: string; bandLower: string; bandShow: string;
  demo: string; faceEmpty: string; faceLogo: string;
  /** « Même couleur partout », the title of the face panels (desktop section), the loupe button. */
  sameInk: string; branches: string; zoom: string; issueOverflowLogo: string; issueHingeLogo: string;
  /** Workshop sheet: one line per view (text, typeface, colour, height, place). */
  sheetPlace: (alongMm: number, acrossMm: number, rot: number) => string;
  sheetEmpty: string; workshopTitle: string; orderCode: string;
}
export interface LensViewWords {
  title: string; opening: string; hint: string; note: string; compare: string; compareAria: string;
  /** A110U: icon button that leaves the view (front camera again). */
  front: string;
  back: string; error: string; denied: string; retry: string;
}
export interface OrderWords {
  /** Panel title (mobile header, desktop section title). */
  title: string;
  /** Accessible name of the photo thumbnail. */
  photo: string; capturing: string; taken: string; photoError: string;
  retake: string; retakeAria: string;
  labels: { model: string; acetate: string; lenses: string; size: string; pd: string };
  /** « 63 mm (estimée) »: PD from the head-size buttons, not typed in. */
  estimated: string;
  /** Lens finish words: « dégradé » (gradient on), « sans dégradé » (an originally gradient lens turned plain). */
  gradient: string; noGradient: string;
  whatsapp: string; whatsappHint: string; save: string; share: string;
  /**
   * A110W — WhatsApp in two steps. Step 1 done (we only know WhatsApp was opened), the link that
   * reopens the chat (+ its screen-reader end), step 2 « send the photo » with its help line (share
   * sheet) or the help of the download fallback, the wait while the JPEG is prepared, the step name
   * for the announcement, and the first words of the shared photo's caption.
   */
  step1Done: string; reopen: string; reopenSr: string; sendPhoto: string; sendHint: string; saveHint: string;
  preparing: string; step2: string; captionLead: string;
  addToCart: string; adding: string; added: string; cartError: string; viewOnSite: string;
  privacy: string; newTab: string;
  /** WhatsApp message: first line, label/value separator, last line. */
  greeting: string; separator: string; photoFollows: string;
  /** Shopify line-item properties (shown in the cart, the checkout and the order). */
  cart: { acetate: string; lenses: string; size: string; pd: string; tryOn: string; yes: string; code: string; forFrame: string };
  /**
   * A201 (David : « PDF + code commande ») — the order code (WhatsApp, cart, photo, PDF), the order sheet as a
   * PDF (its button, help line, title, « Essayage du 9 octobre 2026 », footnote, the second page's title).
   */
  code: string; pdf: string; pdfShort: string; pdfHint: string; pdfTitle: string; pdfDate: (date: string) => string; pdfNote: string; pdfFaces: string;
}

/** A42E interface copy (FR / EN / ID). Model and colourway names are data, never translated. */
const WORDS: Record<'en' | 'fr' | 'id', Words> = {
  fr: {
    pill: ['Taille', 'Couleurs', 'Modèles', 'Je la veux'],
    toolbar: 'Réglages de l’essayage',
    help: 'Comment ça marche ?',
    helpBody: 'La caméra suit votre visage en temps réel. Aucune image n’est envoyée ni enregistrée. La taille de votre visage met la monture à l’échelle ; les couleurs sont un aperçu.',
    helpNote: 'Matières à confirmer avec l’atelier.',
    helpRear: 'La plaque arrière garde sa teinte d’origine.',
    headQuestion: 'Quelle est la taille de votre visage ?',
    faces: ['Petit', 'Moyen', 'Grand'],
    facesKept: ['petit', 'moyen', 'grand'],
    frameSize: 'Largeur de la monture',
    frameSizeShort: 'Largeur',
    pdEstimated: (mm: number) => `PD estimée · ${mm} mm`,
    pdKnown: (mm: number) => `Votre PD · ${mm} mm`,
    pdCalibrating: 'regardez la caméra',
    pdOpen: 'Je connais ma PD',
    pdClose: 'Fermer',
    pdInput: 'Écart pupillaire en mm',
    apply: 'Appliquer',
    useFace: 'Utiliser le visage choisi',
    tabs: ['Acétate', 'Verres', 'Sunset'],
    sections: ['Acétate', 'Verres', 'Verres Sunset', 'Intensité'],
    mainAcetate: 'Acétate principal',
    gradient: 'Dégradé',
    intensity: ['Clair', 'Foncé'],
    original: 'Original',
    originalColours: 'Couleurs d’origine',
    materialsNote: 'Matières à confirmer avec l’atelier',
    sunsetSupplement: price => `Supplément Sunset : + ${price}`,
    acetates: { 'black': 'Noir', 'smoke': 'Fumé', 'sand': 'Sable', 'taupe': 'Taupe', 'cinnamon': 'Cannelle', 'rust': 'Rouille', 'red': 'Rouge', 'burgundy': 'Bordeaux', 'aubergine': 'Aubergine', 'cobalt': 'Cobalt', 'petrol': 'Pétrole', 'olive': 'Olive', 'denim': 'Denim', 'tortoise': 'Écaille', 'honey-tortoise': 'Écaille miel', 'blonde-tortoise': 'Écaille blonde', 'caramel': 'Caramel', 'auburn-tortoise': 'Écaille rousse', 'golden-tortoise': 'Écaille dorée', 'dark-tortoise': 'Écaille sombre', 'jaguar': 'Jaguar', 'green-tortoise': 'Écaille verte', 'blue-tortoise': 'Écaille azur', 'ivory-tortoise': 'Écaille ivoire', 'copper-horn': 'Corne cuivre', 'cream-horn': 'Corne crème', 'glacier-horn': 'Corne glacier', 'crackle': 'Craquelé', 'zebra': 'Zèbre', 'honeycomb': 'Nid d’abeille', 'green-marble': 'Marbre vert', 'wine-marble': 'Marbre bordeaux', 'lilac': 'Lilas', 'pearl-mint': 'Menthe nacrée', 'oat': 'Avoine' },
    acetateFamilies: { essentials: 'Original', transparent: 'Transparents', tortoise: 'Écailles', horn: 'Cornes', fantasy: 'Fantaisie' },
    lenses: { brown: 'Brun', green: 'Vert', grey: 'Gris', blue: 'Bleu', clear: 'Lunettes de vue' },
    kept: (face: string | null, pd: number, size: string | null) => `Gardé : ${face ? `visage ${face}` : `PD ${pd} mm`}${size ? ` · taille ${size}` : ''}`,
    collection: (line: string, n: number) => `${line} · ${n}`,
    view: (model: string) => `Voir ${model}`,
    models: 'Modèles',
    closePanel: 'Fermer le panneau',
    seeAll: 'Tout voir',
    seeAllFrames: 'Tous les modèles',
    collections: {
      switchLabel: 'Collection',
      shape: 'Forme', allShapes: 'Toutes',
      shapes: { 'Rectangle': 'Rectangulaire', 'Cat-eye': 'Œil-de-chat', 'Oval': 'Ovale', 'Aviator': 'Aviateur', 'Round': 'Ronde', 'Butterfly': 'Papillon', 'Square': 'Carrée', 'Hexagonal': 'Hexagonale', 'Oversized': 'Oversize', 'Sculptural': 'Sculpturale' },
      filtered: (n, shape) => `${n} monture${n > 1 ? 's' : ''} · ${shape}`,
    },
    lookThrough: 'Voir le paysage',
    lookThroughShort: 'Paysage',
    preparing: 'Préparation de l’essayage…',
    resuming: 'Reprise de l’essayage…',
    recoverText: 'L’essayage s’est figé pendant la mise en veille.',
    recoverButton: 'Relancer l’essayage',
    reloadText: 'L’essayage ne répond plus correctement. Une actualisation de la page règle le problème.',
    reloadButton: 'Actualiser la page',
    lensView: {
      title: 'Le paysage à travers le verre Sunset',
      opening: 'Ouverture de la caméra arrière…',
      hint: 'Visez le paysage. Maintenez l’écran pour comparer sans verre.',
      note: 'Aperçu des teintes, pas une mesure du verre.',
      compare: 'Sans verre',
      compareAria: 'Maintenir pour voir sans verre',
      front: 'Revenir à la caméra avant',
      back: 'Retour à l’essayage',
      error: 'Impossible d’ouvrir la caméra arrière.',
      denied: 'Autorisez la caméra pour voir le paysage à travers le verre.',
      retry: 'Réessayer',
    },
    order: {
      title: 'Je veux cette paire',
      photo: 'Photo de l’essayage',
      capturing: 'Photo en cours…',
      taken: 'Photo prise',
      photoError: 'La photo n’a pas pu être préparée. Reprenez-la.',
      retake: 'Reprendre',
      retakeAria: 'Reprendre la photo dans 3 secondes',
      labels: { model: 'Modèle', acetate: 'Acétate', lenses: 'Verres', size: 'Taille', pd: 'PD' },
      estimated: 'estimée',
      gradient: 'dégradé',
      noGradient: 'sans dégradé',
      whatsapp: 'Commander sur WhatsApp',
      whatsappHint: 'Étape 2 juste après : la photo de l’essayage.',
      save: 'Enregistrer la photo',
      share: 'Partager la photo',
      step1Done: 'Message préparé dans WhatsApp',
      reopen: 'Rouvrir',
      reopenSr: ' la discussion WhatsApp',
      sendPhoto: 'Envoyer la photo à Maison Mata',
      sendHint: 'Choisissez WhatsApp, puis Maison Mata (en haut de vos discussions).',
      saveHint: 'Puis joignez-la dans la discussion WhatsApp avec Maison Mata.',
      preparing: 'Préparation…',
      step2: 'Étape 2',
      captionLead: 'Ma paire Maison Mata : ',
      addToCart: 'Ajouter au panier',
      adding: 'Ajout…',
      added: 'Ajoutée · Voir le panier',
      cartError: 'Ajout au panier impossible. Écrivez-nous sur WhatsApp.',
      viewOnSite: 'Voir sur maisonmata.com',
      privacy: 'La photo reste sur votre appareil.',
      newTab: 'nouvel onglet',
      greeting: 'Bonjour Maison Mata, je veux cette paire :',
      separator: ' : ',
      photoFollows: 'Je vous envoie la photo et la fiche de commande juste après.',
      cart: { acetate: 'Acétate souhaité', lenses: 'Verres', size: 'Taille', pd: 'PD', tryOn: 'Essayage virtuel', yes: 'Oui', code: 'Code commande', forFrame: 'Pour la monture' },
      code: 'Code commande', pdf: 'Fiche commande (PDF)', pdfShort: 'Fiche PDF', pdfHint: 'Toutes les informations pour l’atelier, avec les quatre faces des branches.',
      pdfTitle: 'Fiche commande', pdfDate: d => `Essayage du ${d}`, pdfFaces: 'Fiche atelier — gravure des quatre faces',
      pdfNote: 'Fiche préparée par l’essayage virtuel Maison Mata. Matières, gravure, prix et délai à confirmer avec l’atelier.',
    },
    engrave: {
      tab: 'Gravure', title: 'Gravure',
      back: 'Essayage', original: 'Original',
      yourTemples: (model: string) => `${model} · Vos branches`,
      inner: 'Intérieur', outer: 'Extérieur',
      temple: (side) => (side === 'l' ? 'B1 · Branche gauche' : 'B2 · Branche droite'),
      slots: { il: 'Intérieur gauche', ir: 'Intérieur droit', el: 'Extérieur gauche', er: 'Extérieur droit' },
      alwaysEngraved: 'Toujours gravé', optional: 'Facultatif',
      tapToEngrave: 'Toucher pour graver', clickToEngrave: 'Cliquer pour graver',
      restoreDefault: 'Remettre « Handcrafted in Bali »',
      counter: (n: number, max: number) => `${n} / ${max}`,
      tooLong: 'Trop long : 20 caractères max. pour rester lisible.',
      stripped: 'Caractère non gravable retiré : choisissez un symbole de la liste.',
      allowed: (symbols: number) => `20 caractères max. Lettres, chiffres, accents, . , - ' & ! ? @ # % + / ( ) € et ${symbols} symboles gravables`,
      colour: 'Couleur',
      inks: { or: 'Or', argent: 'Argent', blanc: 'Blanc', noir: 'Noir', rouge: 'Rouge' },
      lowContrast: 'Peu visible sur cette monture',
      font: 'Police',
      filters: { all: 'Toutes', minimal: 'Minimal', serif: 'Sérif', script: 'Script', fort: 'Fort', machine: 'Machine' },
      sample: 'Bali',
      symbols: 'Symboles', addSymbol: (glyph: string) => `Ajouter ${glyph}`,
      symbolFamilies: { ile: 'Île', amour: 'Amour', ciel: 'Ciel', signes: 'Signes' },
      abc: 'ABC', ok: 'OK', erase: 'Effacer',
      size: 'Hauteur des lettres', smaller: 'Lettres plus petites', larger: 'Lettres plus grandes',
      mm: (v: number) => `${String(v).replace('.', ',')} mm`, sameHeight: 'Même hauteur sur toutes les lignes',
      limitThin: (mm: string) => `Branche fine : ${mm} au plus sur ce modèle.`,
      limitLong: (mm: string) => `Ajusté à ${mm} pour tenir sur la branche.`,
      capOrder: (mm: string) => `lettres ${mm}`,
      position: 'Position', tilt: 'Inclinaison', tiltLeft: 'Tourner vers la gauche', tiltRight: 'Tourner vers la droite',
      deg: (v: number) => `${v === 0 ? '0' : v > 0 ? `+${v}` : `−${-v}`}°`,
      moveLeft: 'Déplacer à gauche', moveUp: 'Déplacer vers le haut', moveDown: 'Déplacer vers le bas', moveRight: 'Déplacer à droite',
      recentre: 'Recentrer', dragHint: 'Faites glisser le texte sur la branche ; la poignée ronde ou deux doigts le font tourner.',
      dragHintBand: 'Faites glisser le texte sur la branche, dans la bande sous le visage ; la poignée ronde le fait tourner.',
      issueOverflow: 'Le texte dépasse de la branche : rapprochez-le du centre ou réduisez la hauteur.',
      issueHinge: 'Le texte touche la charnière : éloignez-le un peu.',
      placeOrder: (x: number, y: number, rot: number) => {
        const n = (v: number) => String(v).replace('.', ',');
        const parts = [`centre à ${n(x)} mm de la charnière`];
        if (y) parts.push(`${n(Math.abs(y))} mm plus ${y > 0 ? 'bas' : 'haut'}`);
        if (rot) parts.push(`incliné de ${Math.abs(rot)}° vers le ${rot > 0 ? 'bas' : 'haut'}`);
        return parts.join(', ');
      },
      sheetTitle: 'Gravure · branches', sheetNote: 'Aperçu : position, taille et couleur à confirmer avec l’atelier.',
      sheetView: (side, face) => `${side === 'l' ? 'Branche gauche' : 'Branche droite'} · ${face === 'int' ? 'intérieur' : 'extérieur'}`,
      texts: 'Textes', recap: 'Récapitulatif',
      noCustom: 'Logo Maison Mata et « Handcrafted in Bali » gravés d’origine.', emptied: 'vide',
      bandHint: 'Cliquez une zone pour la modifier',
      validate: 'Valider la gravure',
      kept: (model: string) => `Gravure conservée sur ${model}`,
      orderLabel: 'Gravure', textsCount: (n: number) => `${n} texte${n > 1 ? 's' : ''}`,
      priceNote: 'Prix et délai de gravure : à confirmer',
      whatsappHint: 'Étape 2 juste après : la photo et la gravure.', edit: 'Modifier la gravure',
      cart: { font: 'Gravure · police', ink: 'Gravure · couleur', slot: (slot) => `Gravure · ${slot === 'il' ? 'logo (intérieur gauche)' : slot === 'ir' ? 'intérieur droit' : slot === 'el' ? 'extérieur gauche' : 'extérieur droit'}` },
      moved: 'placé à la main', undo: 'Annuler', copyOther: 'Même chose sur l’autre branche', copied: to => `Copié sur ${to}`,
      quick: { label: 'Emplacement', hinge: 'Près de la charnière', middle: 'Milieu', ear: 'Vers l’oreille' },
      sameInk: 'Même couleur partout', branches: 'Branches', zoom: 'Loupe',
      issueOverflowLogo: 'Le logo dépasse de la branche : rapprochez-le du centre ou réduisez sa taille.', issueHingeLogo: 'Le logo touche la charnière : éloignez-le un peu.',
      adjust: 'Ajuster', logoSize: 'Taille du logo', logoTilt: 'Inclinaison du logo', logoHint: 'Le logo reste sur la branche : vous pouvez le déplacer, le tourner et changer sa taille.',
      zoomHint: 'Glissez le texte ; la poignée ronde ou deux doigts le font tourner.', zoomClose: 'Fermer la loupe',
      spin: 'Glisser pour tourner', bandLower: 'Baisser les branches', bandShow: 'Afficher les branches',
      demo: 'Faites glisser le texte sur la branche, tournez-le à deux doigts.', faceEmpty: 'Rien', faceLogo: 'Logo Maison Mata',
      sheetPlace: (a, c, r) => `centre à ${a} mm de la charnière, ${c === 0 ? 'au milieu' : `${Math.abs(c)} mm plus ${c > 0 ? 'bas' : 'haut'}`}, ${r === 0 ? 'droit' : `incliné de ${Math.abs(r)}° ${r > 0 ? 'vers le bas' : 'vers le haut'}`}`,
      sheetEmpty: 'rien à graver', workshopTitle: 'Fiche atelier — gravure', orderCode: 'Code commande',
    },
  },
  en: {
    pill: ['Size', 'Colours', 'Frames', 'I want it'],
    toolbar: 'Try-on settings',
    help: 'How does it work?',
    helpBody: 'The camera follows your face in real time. No image is sent or stored. Your face size scales the frame; colours are a preview.',
    helpNote: 'Materials to be confirmed with the workshop.',
    helpRear: 'Rear plate keeps its original tint.',
    headQuestion: 'What’s your head size?',
    faces: ['Small', 'Medium', 'Large'],
    facesKept: ['small', 'medium', 'large'],
    frameSize: 'Frame size',
    frameSizeShort: 'Frame size',
    pdEstimated: (mm: number) => `Estimated PD · ${mm} mm`,
    pdKnown: (mm: number) => `Your PD · ${mm} mm`,
    pdCalibrating: 'look at the camera',
    pdOpen: 'I know my PD',
    pdClose: 'Close',
    pdInput: 'Pupillary distance in mm',
    apply: 'Apply',
    useFace: 'Use selected face',
    tabs: ['Acetate', 'Lenses', 'Sunset'],
    sections: ['Acetate', 'Lenses', 'Sunset lenses', 'Intensity'],
    mainAcetate: 'Main acetate',
    gradient: 'Gradient',
    intensity: ['Light', 'Dark'],
    original: 'Original',
    originalColours: 'Original colours',
    materialsNote: 'Materials to be confirmed with the workshop',
    sunsetSupplement: price => `Sunset supplement: + ${price}`,
    acetates: { 'black': 'Black', 'smoke': 'Smoke', 'sand': 'Sand', 'taupe': 'Taupe', 'cinnamon': 'Cinnamon', 'rust': 'Rust', 'red': 'Red', 'burgundy': 'Burgundy', 'aubergine': 'Aubergine', 'cobalt': 'Cobalt', 'petrol': 'Petrol', 'olive': 'Olive', 'denim': 'Denim', 'tortoise': 'Tortoise', 'honey-tortoise': 'Honey tortoise', 'blonde-tortoise': 'Blonde tortoise', 'caramel': 'Caramel', 'auburn-tortoise': 'Auburn tortoise', 'golden-tortoise': 'Golden tortoise', 'dark-tortoise': 'Dark tortoise', 'jaguar': 'Jaguar', 'green-tortoise': 'Green tortoise', 'blue-tortoise': 'Blue tortoise', 'ivory-tortoise': 'Ivory tortoise', 'copper-horn': 'Copper horn', 'cream-horn': 'Cream horn', 'glacier-horn': 'Glacier horn', 'crackle': 'Crackle', 'zebra': 'Zebra', 'honeycomb': 'Honeycomb', 'green-marble': 'Green marble', 'wine-marble': 'Wine marble', 'lilac': 'Lilac', 'pearl-mint': 'Pearl mint', 'oat': 'Oat' },
    acetateFamilies: { essentials: 'Original', transparent: 'Transparent', tortoise: 'Tortoise', horn: 'Horn', fantasy: 'Fantasy' },
    lenses: { brown: 'Brown', green: 'Green', grey: 'Grey', blue: 'Blue', clear: 'Eyeglasses' },
    kept: (face: string | null, pd: number, size: string | null) => `Kept: ${face ? `${face} face` : `PD ${pd} mm`}${size ? ` · size ${size}` : ''}`,
    collection: (line: string, n: number) => `${line} · ${n}`,
    view: (model: string) => `View ${model}`,
    models: 'Frames',
    closePanel: 'Close panel',
    seeAll: 'See all',
    seeAllFrames: 'See all frames',
    collections: {
      switchLabel: 'Collection',
      shape: 'Shape', allShapes: 'All',
      shapes: { 'Rectangle': 'Rectangle', 'Cat-eye': 'Cat-eye', 'Oval': 'Oval', 'Aviator': 'Aviator', 'Round': 'Round', 'Butterfly': 'Butterfly', 'Square': 'Square', 'Hexagonal': 'Hexagonal', 'Oversized': 'Oversized', 'Sculptural': 'Sculptural' },
      filtered: (n, shape) => `${n} frame${n > 1 ? 's' : ''} · ${shape}`,
    },
    lookThrough: 'See the view',
    lookThroughShort: 'The view',
    preparing: 'Preparing the try-on…',
    resuming: 'Resuming the try-on…',
    recoverText: 'The try-on froze during sleep mode.',
    recoverButton: 'Restart the try-on',
    reloadText: 'The try-on is no longer responding properly. Refreshing the page fixes it.',
    reloadButton: 'Refresh the page',
    lensView: {
      title: 'The view through the Sunset lens',
      opening: 'Opening the rear camera…',
      hint: 'Point at the view. Press and hold the screen to compare without the lens.',
      note: 'A colour preview, not a lens measurement.',
      compare: 'No lens',
      compareAria: 'Press and hold to see without the lens',
      front: 'Back to the front camera',
      back: 'Back to the try-on',
      error: 'Could not open the rear camera.',
      denied: 'Allow the camera to see the view through the lens.',
      retry: 'Try again',
    },
    order: {
      title: 'I want this pair',
      photo: 'Try-on photo',
      capturing: 'Taking the photo…',
      taken: 'Photo taken',
      photoError: 'The photo could not be prepared. Retake it.',
      retake: 'Retake',
      retakeAria: 'Retake the photo in 3 seconds',
      labels: { model: 'Model', acetate: 'Acetate', lenses: 'Lenses', size: 'Size', pd: 'PD' },
      estimated: 'estimated',
      gradient: 'gradient',
      noGradient: 'no gradient',
      whatsapp: 'Order on WhatsApp',
      whatsappHint: 'Step 2 right after: the try-on photo.',
      save: 'Save photo',
      share: 'Share photo',
      step1Done: 'Message ready in WhatsApp',
      reopen: 'Reopen',
      reopenSr: ' the WhatsApp chat',
      sendPhoto: 'Send the photo to Maison Mata',
      sendHint: 'Choose WhatsApp, then Maison Mata (at the top of your chats).',
      saveHint: 'Then attach it in the WhatsApp chat with Maison Mata.',
      preparing: 'Preparing…',
      step2: 'Step 2',
      captionLead: 'My Maison Mata pair: ',
      addToCart: 'Add to cart',
      adding: 'Adding…',
      added: 'Added · View cart',
      cartError: 'Could not add to cart. Message us on WhatsApp.',
      viewOnSite: 'View on maisonmata.com',
      privacy: 'The photo stays on your device.',
      newTab: 'new tab',
      greeting: 'Hello Maison Mata, I want this pair:',
      separator: ': ',
      photoFollows: 'I’ll send you the photo and the order sheet right after.',
      cart: { acetate: 'Requested acetate', lenses: 'Lenses', size: 'Size', pd: 'PD', tryOn: 'Virtual try-on', yes: 'Yes', code: 'Order code', forFrame: 'For the frame' },
      code: 'Order code', pdf: 'Order sheet (PDF)', pdfShort: 'PDF sheet', pdfHint: 'Everything the workshop needs, with the four faces of the temples.',
      pdfTitle: 'Order sheet', pdfDate: d => `Try-on of ${d}`, pdfFaces: 'Workshop sheet — engraving of the four faces',
      pdfNote: 'Sheet prepared by the Maison Mata virtual try-on. Materials, engraving, price and lead time to be confirmed with the workshop.',
    },
    engrave: {
      tab: 'Engraving', title: 'Engraving',
      back: 'Try-on', original: 'Original',
      yourTemples: (model: string) => `${model} · Your temples`,
      inner: 'Inside', outer: 'Outside',
      temple: (side) => (side === 'l' ? 'T1 · Left temple' : 'T2 · Right temple'),
      slots: { il: 'Inside left', ir: 'Inside right', el: 'Outside left', er: 'Outside right' },
      alwaysEngraved: 'Always engraved', optional: 'Optional',
      tapToEngrave: 'Tap to engrave', clickToEngrave: 'Click to engrave',
      restoreDefault: 'Restore “Handcrafted in Bali”',
      counter: (n: number, max: number) => `${n} / ${max}`,
      tooLong: 'Too long: 20 characters max. to stay legible.',
      stripped: 'Non-engravable character removed: pick a symbol from the list.',
      allowed: (symbols: number) => `20 characters max. Letters, digits, accents, . , - ' & ! ? @ # % + / ( ) € and ${symbols} engravable symbols`,
      colour: 'Colour',
      inks: { or: 'Gold', argent: 'Silver', blanc: 'White', noir: 'Black', rouge: 'Red' },
      lowContrast: 'Hard to see on this frame',
      font: 'Typeface',
      filters: { all: 'All', minimal: 'Minimal', serif: 'Serif', script: 'Script', fort: 'Bold', machine: 'Mono' },
      sample: 'Bali',
      symbols: 'Symbols', addSymbol: (glyph: string) => `Add ${glyph}`,
      symbolFamilies: { ile: 'Island', amour: 'Love', ciel: 'Sky', signes: 'Signs' },
      abc: 'ABC', ok: 'OK', erase: 'Delete',
      size: 'Letter height', smaller: 'Smaller letters', larger: 'Larger letters',
      mm: (v: number) => `${v} mm`, sameHeight: 'Same height on every line',
      limitThin: (mm: string) => `Thin temple: ${mm} at most on this model.`,
      limitLong: (mm: string) => `Adjusted to ${mm} to fit the temple.`,
      capOrder: (mm: string) => `letters ${mm}`,
      position: 'Position', tilt: 'Tilt', tiltLeft: 'Turn left', tiltRight: 'Turn right',
      deg: (v: number) => `${v === 0 ? '0' : v > 0 ? `+${v}` : `−${-v}`}°`,
      moveLeft: 'Move left', moveUp: 'Move up', moveDown: 'Move down', moveRight: 'Move right',
      recentre: 'Recentre', dragHint: 'Drag the text along the temple; the round handle or two fingers turn it.',
      dragHintBand: 'Drag the text along the temple, in the band below the face; the round handle turns it.',
      issueOverflow: 'The text runs off the temple: move it towards the middle or lower the height.',
      issueHinge: 'The text touches the hinge: move it away a little.',
      placeOrder: (x: number, y: number, rot: number) => {
        const parts = [`centre ${x} mm from the hinge`];
        if (y) parts.push(`${Math.abs(y)} mm ${y > 0 ? 'lower' : 'higher'}`);
        if (rot) parts.push(`tilted ${Math.abs(rot)}° ${rot > 0 ? 'downwards' : 'upwards'}`);
        return parts.join(', ');
      },
      sheetTitle: 'Engraving · temples', sheetNote: 'Preview: position, size and colour to be confirmed with the workshop.',
      sheetView: (side, face) => `${side === 'l' ? 'Left temple' : 'Right temple'} · ${face === 'int' ? 'inside' : 'outside'}`,
      texts: 'Texts', recap: 'Summary',
      noCustom: 'Maison Mata logo and “Handcrafted in Bali” engraved as standard.', emptied: 'empty',
      bandHint: 'Click a zone to edit it',
      validate: 'Confirm the engraving',
      kept: (model: string) => `Engraving kept on ${model}`,
      orderLabel: 'Engraving', textsCount: (n: number) => `${n} text${n > 1 ? 's' : ''}`,
      priceNote: 'Engraving price and lead time: to be confirmed',
      whatsappHint: 'Step 2 right after: the photo and the engraving.', edit: 'Edit the engraving',
      cart: { font: 'Engraving · typeface', ink: 'Engraving · colour', slot: (slot) => `Engraving · ${slot === 'il' ? 'logo (inside left)' : slot === 'ir' ? 'inside right' : slot === 'el' ? 'outside left' : 'outside right'}` },
      moved: 'placed by hand', undo: 'Undo', copyOther: 'Same on the other temple', copied: to => `Copied to ${to}`,
      quick: { label: 'Placement', hinge: 'Near the hinge', middle: 'Middle', ear: 'Towards the ear' },
      sameInk: 'Same colour everywhere', branches: 'Temples', zoom: 'Magnifier',
      issueOverflowLogo: 'The logo goes past the temple: move it towards the middle or make it smaller.', issueHingeLogo: 'The logo touches the hinge: move it away a little.',
      adjust: 'Adjust', logoSize: 'Logo size', logoTilt: 'Logo tilt', logoHint: 'The logo stays on the temple: you can move it, tilt it and change its size.',
      zoomHint: 'Drag the text; the round handle or two fingers turn it.', zoomClose: 'Close the magnifier',
      spin: 'Drag to turn', bandLower: 'Lower the temples', bandShow: 'Show the temples',
      demo: 'Drag the text along the temple, turn it with two fingers.', faceEmpty: 'Nothing', faceLogo: 'Maison Mata logo',
      sheetPlace: (a, c, r) => `centre ${a} mm from the hinge, ${c === 0 ? 'centred' : `${Math.abs(c)} mm ${c > 0 ? 'lower' : 'higher'}`}, ${r === 0 ? 'level' : `tilted ${Math.abs(r)}° ${r > 0 ? 'down' : 'up'}`}`,
      sheetEmpty: 'nothing to engrave', workshopTitle: 'Workshop sheet — engraving', orderCode: 'Order code',
    },
  },
  id: {
    pill: ['Ukuran', 'Warna', 'Model', 'Saya mau'],
    toolbar: 'Pengaturan coba virtual',
    help: 'Bagaimana caranya?',
    helpBody: 'Kamera mengikuti wajah Anda secara langsung. Tidak ada gambar yang dikirim atau disimpan. Ukuran wajah menyesuaikan skala bingkai; warna hanya pratinjau.',
    helpNote: 'Konfirmasi bahan dengan bengkel.',
    helpRear: 'Pelat belakang tetap berwarna asli.',
    headQuestion: 'Berapa ukuran kepala Anda?',
    faces: ['Kecil', 'Sedang', 'Besar'],
    facesKept: ['kecil', 'sedang', 'besar'],
    frameSize: 'Lebar bingkai',
    frameSizeShort: 'Lebar',
    pdEstimated: (mm: number) => `Perkiraan PD · ${mm} mm`,
    pdKnown: (mm: number) => `PD Anda · ${mm} mm`,
    pdCalibrating: 'lihat ke kamera',
    pdOpen: 'Saya tahu PD saya',
    pdClose: 'Tutup',
    pdInput: 'Jarak pupil dalam mm',
    apply: 'Terapkan',
    useFace: 'Gunakan ukuran wajah',
    tabs: ['Asetat', 'Lensa', 'Sunset'],
    sections: ['Asetat', 'Lensa', 'Lensa Sunset', 'Intensitas'],
    mainAcetate: 'Asetat utama',
    gradient: 'Gradasi',
    intensity: ['Terang', 'Gelap'],
    original: 'Asli',
    originalColours: 'Warna asli',
    materialsNote: 'Konfirmasi bahan dengan bengkel',
    sunsetSupplement: price => `Tambahan Sunset: + ${price}`,
    acetates: { 'black': 'Hitam', 'smoke': 'Asap', 'sand': 'Pasir', 'taupe': 'Taupe', 'cinnamon': 'Kayu manis', 'rust': 'Karat', 'red': 'Merah', 'burgundy': 'Marun', 'aubergine': 'Terong', 'cobalt': 'Kobalt', 'petrol': 'Biru petrol', 'olive': 'Zaitun', 'denim': 'Denim', 'tortoise': 'Kura-kura', 'honey-tortoise': 'Kura-kura madu', 'blonde-tortoise': 'Kura-kura pirang', 'caramel': 'Karamel', 'auburn-tortoise': 'Kura-kura merah', 'golden-tortoise': 'Kura-kura emas', 'dark-tortoise': 'Kura-kura gelap', 'jaguar': 'Jaguar', 'green-tortoise': 'Kura-kura hijau', 'blue-tortoise': 'Kura-kura biru', 'ivory-tortoise': 'Kura-kura gading', 'copper-horn': 'Tanduk tembaga', 'cream-horn': 'Tanduk krem', 'glacier-horn': 'Tanduk es', 'crackle': 'Retak', 'zebra': 'Zebra', 'honeycomb': 'Sarang lebah', 'green-marble': 'Marmer hijau', 'wine-marble': 'Marmer anggur', 'lilac': 'Lila', 'pearl-mint': 'Mint mutiara', 'oat': 'Gandum' },
    acetateFamilies: { essentials: 'Asli', transparent: 'Transparan', tortoise: 'Kura-kura', horn: 'Tanduk', fantasy: 'Fantasi' },
    lenses: { brown: 'Cokelat', green: 'Hijau', grey: 'Abu-abu', blue: 'Biru', clear: 'Kacamata optik' },
    kept: (face: string | null, pd: number, size: string | null) => `Tetap: ${face ? `wajah ${face}` : `PD ${pd} mm`}${size ? ` · ukuran ${size}` : ''}`,
    collection: (line: string, n: number) => `${line} · ${n}`,
    view: (model: string) => `Lihat ${model}`,
    models: 'Model',
    closePanel: 'Tutup panel',
    seeAll: 'Lihat semua',
    seeAllFrames: 'Semua model',
    collections: {
      switchLabel: 'Koleksi',
      shape: 'Bentuk', allShapes: 'Semua',
      shapes: { 'Rectangle': 'Persegi panjang', 'Cat-eye': 'Mata kucing', 'Oval': 'Oval', 'Aviator': 'Aviator', 'Round': 'Bulat', 'Butterfly': 'Kupu-kupu', 'Square': 'Persegi', 'Hexagonal': 'Heksagonal', 'Oversized': 'Oversized', 'Sculptural': 'Skulptural' },
      filtered: (n, shape) => `${n} bingkai · ${shape}`,
    },
    lookThrough: 'Lihat pemandangan',
    lookThroughShort: 'Pemandangan',
    preparing: 'Menyiapkan coba virtual…',
    resuming: 'Melanjutkan coba virtual…',
    recoverText: 'Coba virtual terhenti saat mode tidur.',
    recoverButton: 'Mulai ulang coba virtual',
    reloadText: 'Coba virtual tidak lagi berjalan dengan baik. Menyegarkan halaman akan memperbaikinya.',
    reloadButton: 'Segarkan halaman',
    lensView: {
      title: 'Pemandangan melalui lensa Sunset',
      opening: 'Membuka kamera belakang…',
      hint: 'Arahkan ke pemandangan. Tekan dan tahan layar untuk membandingkan tanpa lensa.',
      note: 'Pratinjau warna, bukan ukuran lensa.',
      compare: 'Tanpa lensa',
      compareAria: 'Tekan dan tahan untuk melihat tanpa lensa',
      front: 'Kembali ke kamera depan',
      back: 'Kembali ke coba virtual',
      error: 'Kamera belakang tidak dapat dibuka.',
      denied: 'Izinkan kamera untuk melihat pemandangan melalui lensa.',
      retry: 'Coba lagi',
    },
    order: {
      title: 'Saya mau kacamata ini',
      photo: 'Foto coba virtual',
      capturing: 'Mengambil foto…',
      taken: 'Foto diambil',
      photoError: 'Foto tidak dapat disiapkan. Ambil ulang.',
      retake: 'Ulangi',
      retakeAria: 'Ambil ulang foto dalam 3 detik',
      labels: { model: 'Model', acetate: 'Asetat', lenses: 'Lensa', size: 'Ukuran', pd: 'PD' },
      estimated: 'perkiraan',
      gradient: 'gradasi',
      noGradient: 'tanpa gradasi',
      whatsapp: 'Pesan via WhatsApp',
      whatsappHint: 'Langkah 2 setelah ini: foto coba virtual.',
      save: 'Simpan foto',
      share: 'Bagikan foto',
      step1Done: 'Pesan siap di WhatsApp',
      reopen: 'Buka lagi',
      reopenSr: ' chat WhatsApp',
      sendPhoto: 'Kirim foto ke Maison Mata',
      sendHint: 'Pilih WhatsApp, lalu Maison Mata (di atas daftar chat Anda).',
      saveHint: 'Lalu lampirkan di chat WhatsApp dengan Maison Mata.',
      preparing: 'Menyiapkan…',
      step2: 'Langkah 2',
      captionLead: 'Kacamata Maison Mata saya: ',
      addToCart: 'Tambah ke keranjang',
      adding: 'Menambahkan…',
      added: 'Ditambahkan · Lihat keranjang',
      cartError: 'Gagal menambahkan ke keranjang. Hubungi kami via WhatsApp.',
      viewOnSite: 'Lihat di maisonmata.com',
      privacy: 'Foto tetap di perangkat Anda.',
      newTab: 'tab baru',
      greeting: 'Halo Maison Mata, saya mau kacamata ini:',
      separator: ': ',
      photoFollows: 'Saya akan mengirim foto dan lembar pesanannya setelah ini.',
      cart: { acetate: 'Asetat pilihan', lenses: 'Lensa', size: 'Ukuran', pd: 'PD', tryOn: 'Coba virtual', yes: 'Ya', code: 'Kode pesanan', forFrame: 'Untuk bingkai' },
      code: 'Kode pesanan', pdf: 'Lembar pesanan (PDF)', pdfShort: 'Lembar PDF', pdfHint: 'Semua informasi untuk bengkel, dengan keempat sisi gagang.',
      pdfTitle: 'Lembar pesanan', pdfDate: d => `Coba virtual ${d}`, pdfFaces: 'Lembar bengkel — ukiran keempat sisi',
      pdfNote: 'Lembar ini dibuat oleh coba virtual Maison Mata. Bahan, ukiran, harga, dan waktu pengerjaan dikonfirmasi dengan bengkel.',
    },
    engrave: {
      tab: 'Ukiran', title: 'Ukiran',
      back: 'Coba virtual', original: 'Asli',
      yourTemples: (model: string) => `${model} · Gagang Anda`,
      inner: 'Dalam', outer: 'Luar',
      temple: (side) => (side === 'l' ? 'G1 · Gagang kiri' : 'G2 · Gagang kanan'),
      slots: { il: 'Dalam kiri', ir: 'Dalam kanan', el: 'Luar kiri', er: 'Luar kanan' },
      alwaysEngraved: 'Selalu diukir', optional: 'Opsional',
      tapToEngrave: 'Sentuh untuk mengukir', clickToEngrave: 'Klik untuk mengukir',
      restoreDefault: 'Kembalikan “Handcrafted in Bali”',
      counter: (n: number, max: number) => `${n} / ${max}`,
      tooLong: 'Terlalu panjang: maks. 20 karakter agar tetap terbaca.',
      stripped: 'Karakter yang tidak bisa diukir dihapus: pilih simbol dari daftar.',
      allowed: (symbols: number) => `Maks. 20 karakter. Huruf, angka, aksen, . , - ' & ! ? @ # % + / ( ) € dan ${symbols} simbol yang bisa diukir`,
      colour: 'Warna',
      inks: { or: 'Emas', argent: 'Perak', blanc: 'Putih', noir: 'Hitam', rouge: 'Merah' },
      lowContrast: 'Kurang terlihat pada bingkai ini',
      font: 'Huruf',
      filters: { all: 'Semua', minimal: 'Minimal', serif: 'Serif', script: 'Skrip', fort: 'Tebal', machine: 'Mono' },
      sample: 'Bali',
      symbols: 'Simbol', addSymbol: (glyph: string) => `Tambah ${glyph}`,
      symbolFamilies: { ile: 'Pulau', amour: 'Cinta', ciel: 'Langit', signes: 'Simbol' },
      abc: 'ABC', ok: 'OK', erase: 'Hapus',
      size: 'Tinggi huruf', smaller: 'Huruf lebih kecil', larger: 'Huruf lebih besar',
      mm: (v: number) => `${String(v).replace('.', ',')} mm`, sameHeight: 'Tinggi sama di semua baris',
      limitThin: (mm: string) => `Gagang tipis: maksimal ${mm} pada model ini.`,
      limitLong: (mm: string) => `Disesuaikan ke ${mm} agar muat di gagang.`,
      capOrder: (mm: string) => `huruf ${mm}`,
      position: 'Posisi', tilt: 'Kemiringan', tiltLeft: 'Putar ke kiri', tiltRight: 'Putar ke kanan',
      deg: (v: number) => `${v === 0 ? '0' : v > 0 ? `+${v}` : `−${-v}`}°`,
      moveLeft: 'Geser ke kiri', moveUp: 'Geser ke atas', moveDown: 'Geser ke bawah', moveRight: 'Geser ke kanan',
      recentre: 'Ke tengah lagi', dragHint: 'Geser teks di gagang; pegangan bulat atau dua jari untuk memutarnya.',
      dragHintBand: 'Geser teks di gagang, pada pita di bawah wajah; pegangan bulat untuk memutarnya.',
      issueOverflow: 'Teks keluar dari gagang: geser ke tengah atau kecilkan hurufnya.',
      issueHinge: 'Teks menyentuh engsel: jauhkan sedikit.',
      placeOrder: (x: number, y: number, rot: number) => {
        const n = (v: number) => String(v).replace('.', ',');
        const parts = [`tengah ${n(x)} mm dari engsel`];
        if (y) parts.push(`${n(Math.abs(y))} mm lebih ${y > 0 ? 'bawah' : 'atas'}`);
        if (rot) parts.push(`miring ${Math.abs(rot)}° ke ${rot > 0 ? 'bawah' : 'atas'}`);
        return parts.join(', ');
      },
      sheetTitle: 'Ukiran · gagang', sheetNote: 'Pratinjau: posisi, ukuran, dan warna dikonfirmasi dengan bengkel.',
      sheetView: (side, face) => `${side === 'l' ? 'Gagang kiri' : 'Gagang kanan'} · ${face === 'int' ? 'dalam' : 'luar'}`,
      texts: 'Teks', recap: 'Ringkasan',
      noCustom: 'Logo Maison Mata dan “Handcrafted in Bali” diukir standar.', emptied: 'kosong',
      bandHint: 'Klik zona untuk mengubahnya',
      validate: 'Konfirmasi ukiran',
      kept: (model: string) => `Ukiran dipertahankan pada ${model}`,
      orderLabel: 'Ukiran', textsCount: (n: number) => `${n} teks`,
      priceNote: 'Harga dan waktu ukiran: akan dikonfirmasi',
      whatsappHint: 'Langkah 2 setelah ini: foto dan ukiran.', edit: 'Ubah ukiran',
      cart: { font: 'Ukiran · huruf', ink: 'Ukiran · warna', slot: (slot) => `Ukiran · ${slot === 'il' ? 'logo (dalam kiri)' : slot === 'ir' ? 'dalam kanan' : slot === 'el' ? 'luar kiri' : 'luar kanan'}` },
      moved: 'diletakkan manual', undo: 'Urungkan', copyOther: 'Sama di gagang lain', copied: to => `Disalin ke ${to}`,
      quick: { label: 'Letak', hinge: 'Dekat engsel', middle: 'Tengah', ear: 'Ke arah telinga' },
      sameInk: 'Warna sama di semua', branches: 'Gagang', zoom: 'Kaca pembesar',
      issueOverflowLogo: 'Logo melewati gagang: geser ke tengah atau perkecil.', issueHingeLogo: 'Logo menyentuh engsel: geser sedikit menjauh.',
      adjust: 'Atur', logoSize: 'Ukuran logo', logoTilt: 'Kemiringan logo', logoHint: 'Logo tetap di gagang: Anda bisa memindahkan, memutar, dan mengubah ukurannya.',
      zoomHint: 'Geser teks; pegangan bulat atau dua jari untuk memutarnya.', zoomClose: 'Tutup kaca pembesar',
      spin: 'Geser untuk memutar', bandLower: 'Turunkan gagang', bandShow: 'Tampilkan gagang',
      demo: 'Geser teks di gagang, putar dengan dua jari.', faceEmpty: 'Kosong', faceLogo: 'Logo Maison Mata',
      sheetPlace: (a, c, r) => `tengah ${a} mm dari engsel, ${c === 0 ? 'di tengah' : `${Math.abs(c)} mm lebih ${c > 0 ? 'bawah' : 'atas'}`}, ${r === 0 ? 'lurus' : `miring ${Math.abs(r)}° ${r > 0 ? 'ke bawah' : 'ke atas'}`}`,
      sheetEmpty: 'tidak ada ukiran', workshopTitle: 'Lembar bengkel — ukiran', orderCode: 'Kode pesanan',
    },
  },
};

export function words(): Words { return ALL_WORDS[getLang()]; }
/** A200: the words of a given language (the catalogue page sets its language before any try-on). */


const ALL_WORDS = expandLocales<Words>(WORDS, (tr: Translate) => ({
    pill: [tr("Size"), tr("Colours"), tr("Frames"), tr("I want it")],
    toolbar: tr("Try-on settings"),
    help: tr("How does it work?"),
    helpBody: tr("The camera follows your face in real time. No image is sent or stored. Your face size scales the frame; colours are a preview."),
    helpNote: tr("Materials to be confirmed with the workshop."),
    helpRear: tr("Rear plate keeps its original tint."),
    headQuestion: tr("What\u2019s your head size?"),
    faces: [tr("Small"), tr("Medium"), tr("Large")],
    facesKept: [tr("small"), tr("medium"), tr("large")],
    frameSize: tr("Frame size"),
    frameSizeShort: tr("Frame size"),
    pdEstimated: (mm: number) => tr("Estimated PD \u00B7 {0} mm", [mm]),
    pdKnown: (mm: number) => tr("Your PD \u00B7 {0} mm", [mm]),
    pdCalibrating: tr("look at the camera"),
    pdOpen: tr("I know my PD"),
    pdClose: tr("Close"),
    pdInput: tr("Pupillary distance in mm"),
    apply: tr("Apply"),
    useFace: tr("Use selected face"),
    tabs: [tr("Acetate"), tr("Lenses"), tr("Sunset")],
    sections: [tr("Acetate"), tr("Lenses"), tr("Sunset lenses"), tr("Intensity")],
    mainAcetate: tr("Main acetate"),
    gradient: tr("Gradient"),
    intensity: [tr("Light"), tr("Dark")],
    original: tr("Original"),
    originalColours: tr("Original colours"),
    materialsNote: tr("Materials to be confirmed with the workshop"),
    sunsetSupplement: price => tr("Sunset supplement: + {0}", [price]),
    acetates: { 'black': tr("Black"), 'smoke': tr("Smoke"), 'sand': tr("Sand"), 'taupe': tr("Taupe"), 'cinnamon': tr("Cinnamon"), 'rust': tr("Rust"), 'red': tr("Red"), 'burgundy': tr("Burgundy"), 'aubergine': tr("Aubergine"), 'cobalt': tr("Cobalt"), 'petrol': tr("Petrol"), 'olive': tr("Olive"), 'denim': tr("Denim"), 'tortoise': tr("Tortoise"), 'honey-tortoise': tr("Honey tortoise"), 'blonde-tortoise': tr("Blonde tortoise"), 'caramel': tr("Caramel"), 'auburn-tortoise': tr("Auburn tortoise"), 'golden-tortoise': tr("Golden tortoise"), 'dark-tortoise': tr("Dark tortoise"), 'jaguar': tr("Jaguar"), 'green-tortoise': tr("Green tortoise"), 'blue-tortoise': tr("Blue tortoise"), 'ivory-tortoise': tr("Ivory tortoise"), 'copper-horn': tr("Copper horn"), 'cream-horn': tr("Cream horn"), 'glacier-horn': tr("Glacier horn"), 'crackle': tr("Crackle"), 'zebra': tr("Zebra"), 'honeycomb': tr("Honeycomb"), 'green-marble': tr("Green marble"), 'wine-marble': tr("Wine marble"), 'lilac': tr("Lilac"), 'pearl-mint': tr("Pearl mint"), 'oat': tr("Oat") },
    acetateFamilies: { essentials: tr("Original"), transparent: tr("Transparent"), tortoise: tr("Tortoise"), horn: tr("Horn"), fantasy: tr("Fantasy") },
    lenses: { brown: tr("Brown"), green: tr("Green"), grey: tr("Grey"), blue: tr("Blue"), clear: tr("Eyeglasses") },
    kept: (face: string | null, pd: number, size: string | null) => tr("Kept: {0}{1}", [face ? tr("{0} face", [face]) : tr("PD {0} mm", [pd]), size ? tr(" \u00B7 size {0}", [size]) : ""]),
    collection: (line: string, n: number) => `${line} · ${n}`,
    view: (model: string) => tr("View {0}", [model]),
    models: tr("Frames"),
    closePanel: tr("Close panel"),
    seeAll: tr("See all"),
    seeAllFrames: tr("See all frames"),
    collections: {
        switchLabel: tr("Collection"),
        shape: tr("Shape"), allShapes: tr("All"),
        shapes: { 'Rectangle': tr("Rectangle"), 'Cat-eye': tr("Cat-eye"), 'Oval': tr("Oval"), 'Aviator': tr("Aviator"), 'Round': tr("Round"), 'Butterfly': tr("Butterfly"), 'Square': tr("Square"), 'Hexagonal': tr("Hexagonal"), 'Oversized': tr("Oversized"), 'Sculptural': tr("Sculptural") },
        filtered: (n, shape) => tr("Frames: {0} \u00B7 {1}", [n, shape]),
    },
    lookThrough: tr("See the view"),
    lookThroughShort: tr("The view"),
    preparing: tr("Preparing the try-on\u2026"),
    resuming: tr("Resuming the try-on\u2026"),
    recoverText: tr("The try-on froze during sleep mode."),
    recoverButton: tr("Restart the try-on"),
    reloadText: tr("The try-on is no longer responding properly. Refreshing the page fixes it."),
    reloadButton: tr("Refresh the page"),
    lensView: {
        title: tr("The view through the Sunset lens"),
        opening: tr("Opening the rear camera\u2026"),
        hint: tr("Point at the view. Press and hold the screen to compare without the lens."),
        note: tr("A colour preview, not a lens measurement."),
        compare: tr("No lens"),
        compareAria: tr("Press and hold to see without the lens"),
        front: tr("Back to the front camera"),
        back: tr("Back to the try-on"),
        error: tr("Could not open the rear camera."),
        denied: tr("Allow the camera to see the view through the lens."),
        retry: tr("Try again"),
    },
    order: {
        title: tr("I want this pair"),
        photo: tr("Try-on photo"),
        capturing: tr("Taking the photo\u2026"),
        taken: tr("Photo taken"),
        photoError: tr("The photo could not be prepared. Retake it."),
        retake: tr("Retake"),
        retakeAria: tr("Retake the photo in 3 seconds"),
        labels: { model: tr("Model"), acetate: tr("Acetate"), lenses: tr("Lenses"), size: tr("Size"), pd: tr("PD") },
        estimated: tr("estimated"),
        gradient: tr("gradient"),
        noGradient: tr("no gradient"),
        whatsapp: tr("Order on WhatsApp"),
        whatsappHint: tr("Step 2 right after: the try-on photo."),
        save: tr("Save photo"),
        share: tr("Share photo"),
        step1Done: tr("Message ready in WhatsApp"),
        reopen: tr("Reopen"),
        reopenSr: tr(" the WhatsApp chat"),
        sendPhoto: tr("Send the photo to Maison Mata"),
        sendHint: tr("Choose WhatsApp, then Maison Mata (at the top of your chats)."),
        saveHint: tr("Then attach it in the WhatsApp chat with Maison Mata."),
        preparing: tr("Preparing\u2026"),
        step2: tr("Step 2"),
        captionLead: tr("My Maison Mata pair: "),
        addToCart: tr("Add to cart"),
        adding: tr("Adding\u2026"),
        added: tr("Added \u00B7 View cart"),
        cartError: tr("Could not add to cart. Message us on WhatsApp."),
        viewOnSite: tr("View on maisonmata.com"),
        privacy: tr("The photo stays on your device."),
        newTab: tr("new tab"),
        greeting: tr("Hello Maison Mata, I want this pair:"),
        separator: ": ",
        photoFollows: tr("I\u2019ll send you the photo and the order sheet right after."),
        cart: { acetate: tr("Requested acetate"), lenses: tr("Lenses"), size: tr("Size"), pd: tr("PD"), tryOn: tr("Virtual try-on"), yes: tr("Yes"), code: tr("Order code"), forFrame: tr("For the frame") },
        code: tr("Order code"), pdf: tr("Order sheet (PDF)"), pdfShort: tr("PDF sheet"), pdfHint: tr("Everything the workshop needs, with the four faces of the temples."),
        pdfTitle: tr("Order sheet"), pdfDate: d => tr("Try-on of {0}", [d]), pdfFaces: tr("Workshop sheet \u2014 engraving of the four faces"),
        pdfNote: tr("Sheet prepared by the Maison Mata virtual try-on. Materials, engraving, price and lead time to be confirmed with the workshop."),
    },
    engrave: {
        tab: tr("Engraving"), title: tr("Engraving"),
        back: tr("Try-on"), original: tr("Original"),
        yourTemples: (model: string) => tr("{0} \u00B7 Your temples", [model]),
        inner: tr("Inside"), outer: tr("Outside"),
        temple: (side) => (side === 'l' ? tr("T1 \u00B7 Left temple") : tr("T2 \u00B7 Right temple")),
        slots: { il: tr("Inside left"), ir: tr("Inside right"), el: tr("Outside left"), er: tr("Outside right") },
        alwaysEngraved: tr("Always engraved"), optional: tr("Optional"),
        tapToEngrave: tr("Tap to engrave"), clickToEngrave: tr("Click to engrave"),
        restoreDefault: tr("Restore \u201CHandcrafted in Bali\u201D"),
        counter: (n: number, max: number) => `${n} / ${max}`,
        tooLong: tr("Too long: 20 characters max. to stay legible."),
        stripped: tr("Non-engravable character removed: pick a symbol from the list."),
        allowed: (symbols: number) => tr("20 characters max. Letters, digits, accents, . , - ' & and {0} engravable symbols", [symbols]).replace(". , - ' &", ". , - ' & ! ? @ # % + / ( ) €"),
        colour: tr("Colour"),
        inks: { or: tr("Gold"), argent: tr("Silver"), blanc: tr("White"), noir: tr("Black"), rouge: tr("Red") },
        lowContrast: tr("Hard to see on this frame"),
        font: tr("Typeface"),
        filters: { all: tr("All"), minimal: tr("Minimal"), serif: tr("Serif"), script: tr("Script"), fort: tr("Bold"), machine: tr("Mono") },
        sample: tr("Bali"),
        symbols: tr("Symbols"), addSymbol: (glyph: string) => tr("Add {0}", [glyph]),
        symbolFamilies: { ile: tr("Island"), amour: tr("Love"), ciel: tr("Sky"), signes: tr("Signs") },
        abc: tr("ABC"), ok: tr("OK"), erase: tr("Delete"),
        size: tr("Letter height"), smaller: tr("Smaller letters"), larger: tr("Larger letters"),
        mm: (v: number) => tr("{0} mm", [v]), sameHeight: tr("Same height on every line"),
        limitThin: (mm: string) => tr("Thin temple: {0} at most on this model.", [mm]),
        limitLong: (mm: string) => tr("Adjusted to {0} to fit the temple.", [mm]),
        capOrder: (mm: string) => tr("letters {0}", [mm]),
        position: tr("Position"), tilt: tr("Tilt"), tiltLeft: tr("Turn left"), tiltRight: tr("Turn right"),
        deg: (v: number) => `${v === 0 ? "0" : v > 0 ? `+${v}` : `−${-v}`}°`,
        moveLeft: tr("Move left"), moveUp: tr("Move up"), moveDown: tr("Move down"), moveRight: tr("Move right"),
        recentre: tr("Recentre"), dragHint: tr("Drag the text along the temple; the round handle or two fingers turn it."),
        dragHintBand: tr("Drag the text along the temple, in the band below the face; the round handle turns it."),
        issueOverflow: tr("The text runs off the temple: move it towards the middle or lower the height."),
        issueHinge: tr("The text touches the hinge: move it away a little."),
        placeOrder: (x: number, y: number, rot: number) => {
            const parts = [tr("centre {0} mm from the hinge", [x])];
            if (y)
                parts.push(tr("{0} mm {1}", [Math.abs(y), y > 0 ? tr("lower") : tr("higher")]));
            if (rot)
                parts.push(tr("tilted {0}\u00B0 {1}", [Math.abs(rot), rot > 0 ? tr("downwards") : tr("upwards")]));
            return parts.join(", ");
        },
        sheetTitle: tr("Engraving \u00B7 temples"), sheetNote: tr("Preview: position, size and colour to be confirmed with the workshop."),
        sheetView: (side, face) => `${side === 'l' ? tr("Left temple") : tr("Right temple")} · ${face === 'int' ? tr("inside") : tr("outside")}`,
        texts: tr("Texts"), recap: tr("Summary"),
        noCustom: tr("Maison Mata logo and \u201CHandcrafted in Bali\u201D engraved as standard."), emptied: tr("empty"),
        bandHint: tr("Click a zone to edit it"),
        validate: tr("Confirm the engraving"),
        kept: (model: string) => tr("Engraving kept on {0}", [model]),
        orderLabel: tr("Engraving"), textsCount: (n: number) => tr("Texts: {0}", [n]),
        priceNote: tr("Engraving price and lead time: to be confirmed"),
        whatsappHint: tr("Step 2 right after: the photo and the engraving."), edit: tr("Edit the engraving"),
        cart: { font: tr("Engraving \u00B7 typeface"), ink: tr("Engraving \u00B7 colour"), slot: (slot) => tr("Engraving \u00B7 {0}", [slot === 'il' ? tr("logo (inside left)") : slot === 'ir' ? tr("inside right") : slot === 'el' ? tr("outside left") : tr("outside right")]) },
        moved: tr("placed by hand"), undo: tr("Undo"), copyOther: tr("Same on the other temple"), copied: to => tr("Copied to {0}", [to]),
        quick: { label: tr("Placement"), hinge: tr("Near the hinge"), middle: tr("Middle"), ear: tr("Towards the ear") },
        sameInk: tr("Same colour everywhere"), branches: tr("Temples"), zoom: tr("Magnifier"),
        issueOverflowLogo: tr("The logo goes past the temple: move it towards the middle or make it smaller."), issueHingeLogo: tr("The logo touches the hinge: move it away a little."),
        adjust: tr("Adjust"), logoSize: tr("Logo size"), logoTilt: tr("Logo tilt"), logoHint: tr("The logo stays on the temple: you can move it, tilt it and change its size."),
        zoomHint: tr("Drag the text; the round handle or two fingers turn it."), zoomClose: tr("Close the magnifier"),
        spin: tr("Drag to turn"), bandLower: tr("Lower the temples"), bandShow: tr("Show the temples"),
        demo: tr("Drag the text along the temple, turn it with two fingers."), faceEmpty: tr("Nothing"), faceLogo: tr("Maison Mata logo"),
        sheetPlace: (a, c, r) => tr("centre {0} mm from the hinge, {1}, {2}", [a, c === 0 ? tr("centred") : tr("{0} mm {1}", [Math.abs(c), c > 0 ? tr("lower") : tr("higher")]), r === 0 ? tr("level") : tr("tilted {0}\u00B0 {1}", [Math.abs(r), r > 0 ? tr("down") : tr("up")])]),
        sheetEmpty: tr("nothing to engrave"), workshopTitle: tr("Workshop sheet \u2014 engraving"), orderCode: tr("Order code"),
    },
}));

export const WORDS_BY_LANG: Record<Lang, Words> = ALL_WORDS;
